import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { isSignInWithEmailLink, signInWithEmailLink, GoogleAuthProvider, linkWithCredential } from "firebase/auth";
import { auth } from "../firebase/config";
import { PENDING_GOOGLE_LINK_KEY, sendJudgeSignInLink } from "../hooks/useAuth";
import logo from "../assets/logo.png";

// Completes the passwordless-email-link invite a tournament admin sends from
// EventDashboard's JUDGES tab (see handleInviteOfficial), and doubles as a
// self-serve "send me a new link" page for anyone returning later without a
// live link (theirs expired, was already used, or they just typed the URL).
// The link's `email` query param comes from the same URL we build for
// sendSignInLinkToEmail, so the common case (judge opens the email on their
// own device) never needs to ask them to retype it — Firebase's own fallback
// (localStorage) only works when the link is opened on the same device/browser
// that requested it, which won't be true for an admin-sent invite.
export default function JudgeRegister() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email") || "");
  // checking | confirm | working | success | error (completing a live link)
  // | request | requesting | sent (self-serve: no live link in the URL)
  const [status, setStatus] = useState("checking");
  const [error, setError] = useState("");
  const [linkedGoogle, setLinkedGoogle] = useState(false);

  useEffect(() => {
    setStatus(isSignInWithEmailLink(auth, window.location.href) ? "confirm" : "request");
  }, []);

  // If this browser has a Google credential parked from useAuth.js's
  // loginWithGoogle (it hit an existing email-link account and sent this
  // link instead), attach Google to the account we just signed into so
  // future sign-ins can use either method.
  const linkPendingGoogleCredential = async (signedInEmail) => {
    const pendingRaw = window.localStorage.getItem(PENDING_GOOGLE_LINK_KEY);
    if (!pendingRaw) return;
    try {
      const pending = JSON.parse(pendingRaw);
      if (pending.email === signedInEmail) {
        const credential = GoogleAuthProvider.credentialFromJSON(pending.credential);
        await linkWithCredential(auth.currentUser, credential);
        setLinkedGoogle(true);
      }
    } catch (err) {
      // credential-already-in-use / provider-already-linked just mean the
      // linking already happened (e.g. a page refresh after success) - not
      // worth surfacing as a failure.
      console.error(err);
    }
    window.localStorage.removeItem(PENDING_GOOGLE_LINK_KEY);
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("working");
    setError("");
    try {
      await signInWithEmailLink(auth, email.trim(), window.location.href);
      await linkPendingGoogleCredential(email.trim());
      setStatus("success");
    } catch (err) {
      console.error(err);
      const messages = {
        "auth/invalid-action-code": "This link has already been used or has expired. Request a new one below.",
        "auth/invalid-email": "That doesn't look like a valid email address.",
        "auth/user-disabled": "This account has been disabled.",
      };
      setError(messages[err.code] || "Something went wrong. Try again, or request a new link below.");
      setStatus("error");
    }
  };

  const handleRequestLink = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("requesting");
    setError("");
    try {
      await sendJudgeSignInLink(email.trim());
      setStatus("sent");
    } catch (err) {
      console.error(err);
      setError(err.code === "auth/invalid-email" ? "That doesn't look like a valid email address." : "Failed to send link. Try again.");
      setStatus("request");
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-6">
      <Link to="/" className="text-gray-600 text-sm mb-8 no-underline hover:text-gray-400">← Home</Link>

      <div className="text-center mb-8">
        <img src={logo} alt="" className="w-full max-w-xs mx-auto" />
        <div className="flex items-center gap-3 max-w-[280px] mx-auto mt-2 mb-3">
          <div className="flex-1 h-px bg-dark-500" />
          <span className="text-gray-400 text-[10px] font-display font-bold tracking-[0.25em] whitespace-nowrap">
            STUDY &bull; FENCE &bull; GROW
          </span>
          <div className="flex-1 h-px bg-dark-500" />
        </div>
        <h1 className="font-display text-2xl font-bold">Judge Sign In</h1>
      </div>

      <div className="w-full max-w-xs flex flex-col gap-4">
        {status === "checking" && (
          <p className="text-gray-500 text-sm text-center">Checking your link…</p>
        )}

        {(status === "confirm" || status === "working" || status === "error") && (
          <form onSubmit={handleConfirm} className="flex flex-col gap-3">
            <p className="text-gray-500 text-sm text-center">
              Confirm your email to finish signing in.
            </p>
            <label htmlFor="judge-confirm-email" className="text-sm text-gray-300">Email</label>
            <input id="judge-confirm-email" aria-invalid={Boolean(error)} value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Email" type="email" autoComplete="email"
              className="w-full p-3 bg-dark-800 border border-dark-500 rounded-lg text-white text-sm outline-none focus:border-gray-400" />

            {error && <div role="alert" className="text-red-400 text-sm text-center bg-red-900 bg-opacity-20 rounded-lg p-2">{error}</div>}

            <button type="submit" disabled={status === "working" || !email.trim()}
              className="w-full p-3.5 bg-gradient-to-br from-green-action-light to-green-action text-white font-display text-sm font-bold rounded-xl tracking-wide disabled:opacity-40 border-none cursor-pointer">
              {status === "working" ? "Confirming..." : "Confirm & Sign In"}
            </button>

            {status === "error" && (
              <button type="button" onClick={() => setStatus("request")}
                className="bg-transparent border-none text-gray-500 text-sm cursor-pointer hover:text-gray-300">
                Request a new link instead
              </button>
            )}
          </form>
        )}

        {(status === "request" || status === "requesting") && (
          <form onSubmit={handleRequestLink} className="flex flex-col gap-3">
            <p className="text-gray-500 text-sm text-center">
              Enter your email and we'll send you a sign-in link.
            </p>
            <label htmlFor="judge-request-email" className="text-sm text-gray-300">Email</label>
            <input id="judge-request-email" aria-invalid={Boolean(error)} value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Email" type="email" autoComplete="email"
              className="w-full p-3 bg-dark-800 border border-dark-500 rounded-lg text-white text-sm outline-none focus:border-gray-400" />

            {error && <div role="alert" className="text-red-400 text-sm text-center bg-red-900 bg-opacity-20 rounded-lg p-2">{error}</div>}

            <button type="submit" disabled={status === "requesting" || !email.trim()}
              className="w-full p-3.5 bg-gradient-to-br from-green-action-light to-green-action text-white font-display text-sm font-bold rounded-xl tracking-wide disabled:opacity-40 border-none cursor-pointer">
              {status === "requesting" ? "Sending..." : "Send Sign-In Link"}
            </button>
          </form>
        )}

        {status === "sent" && (
          <div className="text-center">
            <div className="text-3xl mb-2">✉</div>
            <p className="text-white font-display font-bold mb-1">Check your email</p>
            <p className="text-gray-500 text-sm">We sent a sign-in link to {email}.</p>
          </div>
        )}

        {status === "success" && (
          <div className="text-center">
            <div className="text-3xl mb-2">✓</div>
            <p className="text-white font-display font-bold mb-1">You're signed in!</p>
            {linkedGoogle && (
              <p className="text-gray-500 text-sm mb-1">Your Google account is now linked — you can use it to sign in next time.</p>
            )}
            <p className="text-gray-500 text-sm">You can close this page now.</p>
          </div>
        )}
      </div>
    </div>
  );
}
